import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Mail, Calendar, Code, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function NewsletterAdmin() {
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const handleSendDigest = async () => {
    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-weekly-digest', {
        body: {},
      });

      if (error) throw error;

      toast({
        title: 'Digest Sent Successfully',
        description: `Sent to ${data.successCount} subscribers`,
      });
    } catch (error: any) {
      console.error('Error sending digest:', error);
      toast({
        title: 'Failed to Send Digest',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const cronSQL = `-- Enable required extensions
CREATE EXTENSION IF NOT EXISTS pg_cron;
CREATE EXTENSION IF NOT EXISTS pg_net;

-- Create the weekly digest cron job (runs every Monday at 9 AM UTC)
SELECT cron.schedule(
  'send-weekly-digest',
  '0 9 * * 1',
  $$
  SELECT
    net.http_post(
        url:='https://htvdordrajveyklejsvs.supabase.co/functions/v1/send-weekly-digest',
        headers:='{"Content-Type": "application/json", "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh0dmRvcmRyYWp2ZXlrbGVqc3ZzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ0MTY1MjgsImV4cCI6MjA2OTk5MjUyOH0.4lWZG_fhrC2_ifIc-gOKZEsXagkbGaGYGOib7UPwYCk"}'::jsonb,
        body:='{}'::jsonb
    ) as request_id;
  $$
);

-- View all scheduled cron jobs
SELECT * FROM cron.job;`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Newsletter Administration</h1>
          <p className="text-muted-foreground">Manage your weekly blog digest emails</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send Weekly Digest
            </CardTitle>
            <CardDescription>
              Manually trigger the weekly digest email to all subscribers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSendDigest} disabled={isSending} size="lg">
              {isSending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Digest Now
                </>
              )}
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              This will send an email with the top 5 most popular articles from the last 7 days
              to all subscribed newsletter recipients.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Automated Schedule Setup
            </CardTitle>
            <CardDescription>
              Set up automatic weekly emails every Monday at 9 AM UTC
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Code className="h-4 w-4" />
              <AlertDescription>
                To enable automated weekly digests, run this SQL in your Supabase SQL Editor:
              </AlertDescription>
            </Alert>

            <div className="relative">
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs">
                <code>{cronSQL}</code>
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => {
                  navigator.clipboard.writeText(cronSQL);
                  toast({
                    title: 'Copied to Clipboard',
                    description: 'SQL code copied successfully',
                  });
                }}
              >
                Copy SQL
              </Button>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Go to your Supabase Dashboard → SQL Editor</li>
                <li>Paste the SQL code above</li>
                <li>Click "Run" to create the cron job</li>
                <li>Verify it was created by checking the cron.job table</li>
              </ol>
            </div>

            <Button
              variant="outline"
              onClick={() => window.open('https://supabase.com/dashboard/project/htvdordrajveyklejsvs/sql/new', '_blank')}
            >
              Open SQL Editor
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>What Gets Sent?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>The weekly digest includes:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
              <li>Top 5 most viewed articles from the past 7 days</li>
              <li>Article titles, excerpts, and featured images</li>
              <li>View counts and categories</li>
              <li>Direct links to read each article</li>
              <li>Call-to-action to explore more articles</li>
              <li>Unsubscribe link for recipients</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
